import { Form } from "@/components/ui/form";
import { DEFAULT_STORE_ID } from "@/constants/product-attributes";
import { calculateOrderTotals } from "@/lib/utils";
import { useCustomer } from "@/services/customer-service";
import { useProductVariants } from "@/services/product-variant-service";
import { FulfillmentMethod } from "@/types/fulfillment-type";
import { OrderCreateFormValues } from "@/types/order-type";
import { Grid, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { CustomerSection } from "./components/customer-section";
import { FulfillmentSection } from "./components/fulfillment-section";
import { HiddenFieldsSection } from "./components/hidden-fields-section";
import { LineItemsSection } from "./components/line-items-section";
import { SummarySection } from "./components/summary-section";

interface OrderFormProps {
  form: UseFormReturn<OrderCreateFormValues>;
  onSubmit: (data: OrderCreateFormValues) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  mode?: "create" | "edit";
}

export function OrderForm({
  form,
  onSubmit,
  onCancel,
  isLoading = false,
  mode = "create",
}: OrderFormProps) {
  const watchItems = form.watch("items");
  const watchCustomerId = form.watch("customerId");
  const watchFulfillmentMethod = form.watch("fulfillment.method");
  const watchFulfillmentPrice = form.watch("fulfillment.price");

  // Fetch selected customer when customerId changes
  const { data: selectedCustomer } = useCustomer(watchCustomerId || null);

  // Clear carrier and price when fulfillment method changes to PICKUP
  useEffect(() => {
    if (watchFulfillmentMethod === FulfillmentMethod.PICKUP) {
      form.setValue("fulfillment.carrier", undefined);
      form.setValue("fulfillment.price", undefined);
    }
  }, [watchFulfillmentMethod, form]);

  const { data: variantsData } = useProductVariants();
  const allVariants = useMemo(() => variantsData || [], [variantsData]);

  // Calculate totals for display (not stored in form since not in DTO)
  const calculateTotal = useCallback(() => {
    const items = form.getValues("items") || [];
    const fulfillmentPrice = parseFloat(
      form.getValues("fulfillment.price") || "0"
    );

    const itemsForCalc = items.map((i) => {
      const v = allVariants.find((v) => String(v.id) === String(i.variantId));
      const unitPrice = v ? Number(v.price || 0) : 0;
      return { unitPrice, quantity: i.quantity };
    });

    return calculateOrderTotals({
      items: itemsForCalc,
      discountPercentage: 0,
      deliveryFee: fulfillmentPrice,
    });
  }, [form, allVariants]);

  // Calculate summary values for display
  const totals = useMemo(
    () => calculateTotal(),
    [calculateTotal, watchFulfillmentPrice, JSON.stringify(watchItems)]
  );
  const subtotal = totals.itemsSubtotal;
  const tax = 0; // Tax calculation can be added later if needed
  const taxIncl = tax;
  const total = totals.total;
  const paymentTotal = 0; // Payment status not in DTO, so always 0
  const outstandingBalance = total;
  const deliveryTotal = parseFloat(watchFulfillmentPrice || "0");

  return (
    <Stack spacing={3}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Stack spacing={3}>
                  <LineItemsSection
                    form={form}
                    watchItems={watchItems}
                    onItemsChange={calculateTotal}
                  />
                  {/* <ShipmentsSection /> */}
                  <SummarySection
                    subtotal={subtotal}
                    tax={tax}
                    taxIncl={taxIncl}
                    total={total}
                    paymentTotal={paymentTotal}
                    outstandingBalance={outstandingBalance}
                    deliveryTotal={deliveryTotal}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Stack spacing={3}>
                  <CustomerSection
                    form={form}
                    selectedCustomer={selectedCustomer || null}
                    watchCustomerId={watchCustomerId || ""}
                    onClearCustomer={() => {
                      form.setValue("customerId", "");
                    }}
                  />
                  {/* <AddressSection
                    title="Shipping Address"
                    customer={selectedCustomer}
                    emptyLabel="New Shipping Address"
                  />
                  <AddressSection
                    title="Billing Address"
                    customer={selectedCustomer}
                    emptyLabel="New Billing Address"
                  /> */}
                  <FulfillmentSection form={form} />
                </Stack>
              </Grid>
            </Grid>
            <HiddenFieldsSection form={form} />
          </Stack>
        </form>
      </Form>
    </Stack>
  );
}
